<template>
  <g ref="group"> </g>
</template>

<script lang="ts" setup>
import * as d3 from 'd3'
import { ref, toRefs } from 'vue'
import { useComponent } from './useComponent'
import { Font } from '.'

export type AxisPosition = 'left' | 'right' | 'top' | 'bottom'

export type AxisProps = {
  position: AxisPosition
  ticks: number[]
  domain: [number, number]
  showTicks?: boolean
  showLine?: boolean
  tickLabels: string[]
  tickAligns?: ('start' | 'middle' | 'end')[]
  label?: string
  font?: Partial<Font>
  rotate?: number
}

const fontDefaults = () => ({
  size: 10,
  family: 'Inter',
  weight: 400,
  lineHeight: 12
})

const props = withDefaults(defineProps<AxisProps>(), {
  showTicks: true,
  showLine: true,
  label: undefined,
  format: undefined,
  tickAligns: undefined,
  rotate: undefined,
  font: () => ({
    size: 10,
    family: 'Inter',
    weight: 500,
    lineHeight: 12
  })
})

const { position, showTicks, showLine, ticks, tickLabels, font, domain, tickAligns, rotate } =
  toRefs(props)

const group = ref<SVGGElement | null>(null)

useComponent(group, ({ width, height, group }) => {
  const axisElement = group.append('g')

  const vertical = position.value === 'left' || position.value === 'right'

  const size = vertical ? height : width

  const scale = d3
    .scaleLinear()
    .domain(vertical ? [domain.value[1], domain.value[0]] : domain.value)
    .range([0, size])

  const createTicks = (axis: d3.Axis<d3.NumberValue>) =>
    axis.tickValues(ticks.value).tickFormat((d, i) => tickLabels.value[i])

  const createHorizontal = (top: boolean) =>
    createTicks(top ? d3.axisTop(scale) : d3.axisBottom(scale))

  const createVertical = (left: boolean) =>
    createTicks(left ? d3.axisLeft(scale) : d3.axisRight(scale))

  const axis = vertical
    ? createVertical(position.value === 'left')
    : createHorizontal(position.value === 'top')

  if (position.value === 'bottom') {
    axisElement.attr('transform', `translate(0, ${height})`)
  } else if (position.value === 'top') {
    axisElement.attr('transform', `translate(0, 0)`)
  } else if (position.value === 'left') {
    axisElement.attr('transform', `translate(0, 0)`)
  } else if (position.value === 'right') {
    axisElement.attr('transform', `translate(${width}, 0)`)
  } else {
    throw new Error(`Invalid position: ${position.value}`)
  }

  axisElement.call(axis)

  if (showTicks.value === false) axisElement.selectAll('.tick').selectAll('line').remove()
  if (showLine.value === false) axisElement.select('.domain').remove()

  const fontValue = {
    ...fontDefaults(),
    ...font.value
  }

  axisElement.selectAll('.tick').each(function (d, i) {
    d3.select(this)
      .select('text')
      .attr('text-anchor', tickAligns?.value?.[i] ?? (vertical ? 'end' : 'middle'))
      .attr('font-size', `${fontValue.size}px`)
      .attr('font-family', fontValue.family)
      .attr('font-weight', fontValue.weight)
      .attr('line-height', `${fontValue.lineHeight}px`)
      .style(
        'transform',
        rotate?.value
          ? `translateY(${Math.floor(rotate.value / 4)}px) rotate(${-rotate.value}deg)`
          : ''
      )
  })
})
</script>
